import { Contract, FormInput } from 'components/molecules';

import React from 'react';
import { useFormikContext } from 'formik';
import {
  CONTRACT_STATUS,
  IContractResponse as IServiceContractResponse,
} from 'services/contract/types';
import { DistributionSection } from 'components/molecules/contract/section/DistributionSection';
import { ContractSection } from 'components/molecules/contract/section/ContractSection';
import { Button } from 'components/atoms';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { PiCheck, PiX } from 'react-icons/pi';
import { useAcceptContract, useGetContract, useRejectContract } from 'services/contract';
import { useParamId } from 'hooks/useParamId';
import { t } from 'i18next';
import { useIsUserBanned } from 'hooks/useIsUserBanned';
import { useQueryClient } from '@tanstack/react-query';
import PopupTwoFactor from 'components/molecules/popup/two-factor/PopupTwoFactor';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import { useGetTwoFactorStatus } from 'services/twoFactor';
import { useStoreState } from 'providers/StoreStateContext';
import { ContractSectionsRenderer } from 'components/molecules/contract/section/ContractSectionsRender';

const ContractFormTab = () => {
  const { values } = useFormikContext<IServiceContractResponse>();
  const { tokenData } = useStoreState();

  const queryClient = useQueryClient();
  const twoFactorStatus = useGetTwoFactorStatus(tokenData?.UserId, true);
  const acceptContract = useAcceptContract();
  const rejectContract = useRejectContract();
  const { isBanned } = useIsUserBanned();

  const handleReject = async () => {
    await rejectContract.mutateAsync(
      { contractId: values.id },
      {
        onSuccess: async () =>
          await queryClient.invalidateQueries({
            queryKey: ['contract', values.id],
          }),
        onError: (error) => onErrorShowAllErrorsToast(error),
      },
    );
  };

  const handleAccept = async () => {
    await acceptContract.mutateAsync(
      { contractId: values.id },
      {
        onSuccess: async () =>
          await queryClient.invalidateQueries({
            queryKey: ['contract', values.id],
          }),
        onError: (error) => onErrorShowAllErrorsToast(error),
      },
    );
  };
  const isTwoFactorEnabled = !!twoFactorStatus.data?.isTwoFactorEnabled;

  return (
    <div className="flex flex-col items-center gap-[20px] w-full    py-10">
      {values.status === CONTRACT_STATUS.offer && !isBanned && (
        <Contract.Header>
          {/* REJECT */}
          {isTwoFactorEnabled ? (
            <PopupTwoFactor
              mode="otp-only"
              trigger={
                <Button fullWidth variant={BUTTON_VARIANT.background}>
                  <Button.Icon icon={<PiX />} />
                  <Button.Text text={t('button.reject')} />
                </Button>
              }
              header={
                <>
                  <PiX /> {t('button.reject')}
                </>
              }
              submitLabelKey="button.reject"
              submitOtp={(code, popup) =>
                rejectContract.mutateAsync(
                  { contractId: values.id, code },
                  {
                    onSuccess: async () => {
                      await queryClient.invalidateQueries({
                        queryKey: ['contract', values.id],
                      });
                      popup?.close();
                    },
                  },
                )
              }
            >
              <FormInput.Text
                appBackground
                name="code"
                label={t('input.label.code')}
                type="string"
              />
            </PopupTwoFactor>
          ) : (
            <Button onClick={handleReject} fullWidth variant={BUTTON_VARIANT.background}>
              <Button.Icon icon={<PiX />} />
              <Button.Text text={t('button.reject')} />
            </Button>
          )}

          {/* ACCEPT */}
          {isTwoFactorEnabled ? (
            <PopupTwoFactor
              mode="otp-only"
              trigger={
                <Button fullWidth variant={BUTTON_VARIANT.submit}>
                  <Button.Icon icon={<PiCheck />} />
                  <Button.Text text={t('button.accept')} />
                </Button>
              }
              header={
                <>
                  <PiCheck /> {t('button.accept')}
                </>
              }
              submitLabelKey="button.accept"
              submitOtp={(code, popup) =>
                acceptContract.mutateAsync(
                  { contractId: values.id, code }, // code tylko gdy 2FA włączone
                  {
                    onSuccess: async () => {
                      await queryClient.invalidateQueries({
                        queryKey: ['contract', values.id],
                      });
                      popup?.close();
                    },
                  },
                )
              }
            >
              <FormInput.Text
                appBackground
                name="code"
                label={t('input.label.code')}
                type="string"
              />
            </PopupTwoFactor>
          ) : (
            <Button onClick={handleAccept} fullWidth variant={BUTTON_VARIANT.submit}>
              <Button.Icon icon={<PiCheck />} />
              <Button.Text text={t('button.accept')} />
            </Button>
          )}
        </Contract.Header>
      )}
      <Contract.Page>
        <div>
          <h2 className="font-semibold  w-full text-[24px]">{values.title}</h2>
          <p className="text-[#202A3Ba6]   w-full text-[14px] ">
            {values.startDate ? new Date(values.startDate).toLocaleDateString('pl-PL') : ''}{' '}
            {values.endDate ? `- ${new Date(values.endDate).toLocaleDateString('pl-PL')}` : ''}
          </p>
        </div>

        <ContractSectionsRenderer sections={values.sortedSections ?? []} />
      </Contract.Page>
    </div>
  );
};

export default ContractFormTab;
